<template>
  <div class="ApplicationList grey lighten-6">
    <v-row class="pa-sm-2 pt-md-2 pr-md-0 pb-md-2 pl-md-0">
      <v-spacer />
      <v-col cols="3">
        <v-text-field
          v-model="searchValue"
          prepend-inner-icon="search"
          placeholder="Find Client"
          clearable
          hide-details
          class="searchTxtField mt-2 mr-4"
          aria-label="Find Client"
          @input="performSearchDebounced"          
        />
      </v-col>
      <v-col  cols="auto">
        <v-btn
          class="mt-3 mr-0 ml-3"
          x-large
          :disabled="this.selectedId == ''"
          :aria-label="downloadBtnAriaLabel"
          @click="openDownloadModal"
          data-testid="eaDownloadButton"
        >
          <v-icon class="mr-2">mdi-download-outline</v-icon>
          Download application
        </v-btn>
      </v-col>
    </v-row>

    <!-- ea Filters -->
    <v-row class="pb-3">
      <v-col class="pt-0">
        <span class="filterLabel">Filter by:</span>
        <span class="pl-3"
          ><ListViewFilter
            label="Status"
            type="checkbox"
            :filterOptions="statusFilters"
            @updatefilter="updateStatusFilter"
            ref="statusFilter"
            @menuOpened="sendFilterAnalytics"
        /></span>
        <span class="pl-3"
          ><DateRangeFilter
            ref="startDateFilter"
            label="Received date"
            :filterOptions="startDateFilter"
            @updatefilter="updateStartDateFilter"
            @menuOpened="sendFilterAnalytics"
        /></span>
        <ClearFilters @click="clearAllFilters()" v-if="isFiltered" />
        <SearchResults
          class="data-table-header search-results-number mr-5"
          v-if="isSearchDisplayed"
          :search-results="applications.length"
          :search-term="searchValue"
          :page-length="pageLength"
          search-mode="EA"
          inline
        />
      </v-col>
    </v-row>

    <v-card>
     <v-radio-group v-model="selectedId">  
      <v-data-table
        class="EA"
        :loading="loading"
        :items="applications"
        :headers="headers"        
        :search="searchValue"
        sort-by="dateReceived"
        :no-data-text="
          noResultsFound
            ? 'No results found. Try removing some filters or clearing the keyword search.'
            : 'No data available'
        "
        must-sort
        sort-desc
        hide-default-footer
        :items-per-page="pageLength"
        @current-items="setFilteredIds"
      >

        <template slot="item" slot-scope="props">
          <!-- Table template for applications -->
          <tr
            @click="openApplication(props.item)"
            class="grey--text text--lighten-1"
          >
            <td class="text-xs-left" @click.stop>
                <v-radio
                  :value="props.item.applicationId"                 
                  :aria-label="
                    `Add application for ${props.item.name} to PDF download.`
                  "
                  class="ma-0 pa-0"
                ></v-radio>
            </td>

            <td class="text-xs-right">
              {{ props.item.dateReceived}}
            </td>

            <td class="text-xs-right">
              <a
                :href="`${getOpenApplicationHref(props.item)}`"
                :aria-label="`${props.item.name}. Open application details.`"
                :ref="`itemfocusRef${props.index}`"
              >
                {{ props.item.name }}
              </a>
            </td>
            <td class="text-xs-right">
              {{props.item.application.client.phoneNumber }}
            </td>
            <td class="text-xs-right">
              {{ props.item.application.client.email }}
            </td>
            <td class="text-xs-right">
              <ApplicationStatusChip :status="props.item.applicationStatus" />
            </td>
            <td class="text-sm-center text-xs-left">
              {{props.item.staffUserId }}
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-radio-group>
      <!-- "Load more" button for front-end pagination  -->
      <div v-if="isShowLoadMore" class="loadMoreBar">
        <AdsButton
          class="loadMore"
          primary
          @click="loadMore"
          icon="keyboard_arrow_down"
          buttonText="Load more"
        />

        <span class="loadMoreCount">
          Showing {{ pageLength }} of {{ totalApplicationsDisplayed }}
          <span v-if="searchValue">search results</span>
          <span v-else class="text-lowercase">ea applications</span>
        </span>
      </div>

      <SearchResults
        class="data-table-footer"
        v-if="isShowSearchFooter"
        :search-results="applications.length"
        :search-term="searchValue"
        :page-length="pageLength"
        search-mode="EA"
      />
    </v-card>

    <EaDownloadDocuments
      v-if="showDownloadModal"
      :applications="selectedIdsForDownload"
      @close="showDownloadModal = false"
    />
  </div>
</template>

<script>
import SearchResults from '@/components/applicationList/SearchResults.vue'
import EaDownloadDocuments from '@/components/applicationList/EADownloadDocuments.vue'
import ApplicationStatusChip from '@/components/application/ApplicationStatusChip.vue'
import ListViewFilter from '@/components/app/ListViewFilter.vue'
import DateRangeFilter from '@/components/app/DateRangeFilter.vue'
import ClearFilters from './ClearFilters.vue'

import { mapGetters } from 'vuex'
import { searchFilter } from '@/helpers/searchHelper'
import { debounce } from 'lodash'
import EaCheckbox from '@/components/app/EaCheckbox'
import  AdsButton  from '@/components/app/AdsButton'

import {
  APPLICATION_TYPE,
  EA_STATUS,
  EA_STATUS_ABBREV
} from '@/constants'

const defaultPageLength = 60

const headers = [
  {
    value: 'selectAll',
    align: 'center',
    class: 'select-all',
    sortable: false
  },
  {
    text: 'Received',
    value: 'receivedDate',
    class: 'received-date',
    align: 'start'
  },
  {
    text: 'Full name',
    value: 'name',
    class: 'name',
    align: 'start'
  },
  {
    text: 'Phone Number',
    value: 'phoneNumber',
    class: 'name',
    align: 'start'
  },
  {
    text: 'Email',
    value: 'email',
    class: 'name',
    align: 'start'
  },
  {
    text: 'Status',
    value: 'applicationStatus',
    class: 'status',
    align: 'start'
  },
    {
    text: 'Assinged Staff',
    value: 'staffUserId',
    class: 'name',
    align: 'center'
  }
]

export default {
  name: 'EaDataTable',
  components: {
    SearchResults,
    EaDownloadDocuments,
    ApplicationStatusChip,
    ListViewFilter,
    DateRangeFilter,
    EaCheckbox,
    ClearFilters,
    AdsButton
  },
  data() {
    return {
      loading: false,
      headers,
      selectedId: '',
      filtersCleared: false,
      searchValue: '',
      pageLength: defaultPageLength,
      showDownloadModal: false,
      selectedStatus: [],
      selectedStartDateRange: [],
      APPLICATION_TYPE
    }
  },
  activated() {
    this.resetPageLength()
    this.searchValue = this.searchApplicationsFilter
    // refresh the list in case the status changed in the details window
  },
  created() {
    this.searchValue = this.searchApplicationsFilter
    // load the list on first load
  },
  watch: {
  },
  methods: {   
    loadMore() {
      let newRecordValue = this.pageLength
      this.pageLength += defaultPageLength
      this.$nextTick(() => {
        this.$refs[`itemfocusRef${newRecordValue}`].focus()
      })
    },
    resetPageLength() {
      this.pageLength = defaultPageLength
    },
    getOpenApplicationHref(item) {
      return `/application/${item.applicationId}`
    },
    openApplication(item) {
      this.$router.push(`/${this.getOpenApplicationHref(item)}`)
    },
    setFilteredIds(items) {
      const ids = items.map(item => item.applicationId)
      this.$store.dispatch('set', ['filteredApplicationIds', ids])
    },
    openDownloadModal() {
      this.showDownloadModal = true
    },
    closeDownloadModal() {
      this.showDownloadModal = false
    },
    performSearch() {
      this.$store.commit('setApplicationsFilter', this.searchValue)
      this.resetPageLength()
      // clear selected applications for download pdf on change of keyword filter
       this.selectedId = ''
    },
    performSearchDebounced: debounce(
      function(value) {
        // IE11 clears the checkboxes even when nothing changes so check if something changed
        if (value !== this.searchApplicationsFilter) {
          this.performSearch()
        }
      },
      300,
      { maxWait: 1000 }
    ),
    updateStatusFilter(selectedOptions) {
      this.selectedStatus = selectedOptions
      // clear selected applications for download pdf on change of status filter
      this.selectedId = ''
    },
    updateStartDateFilter(dateRange) {
      if (dateRange !== null && dateRange.length > 0) {
        this.selectedStartDateRange = dateRange
      } else {
        this.selectedStartDateRange = []
      }
      // clear selected applications for download pdf on change of StartDate filter
       this.selectedId = ''
    },
    clearAllFilters() {
      // clear all filtered values
      this.searchValue = ''
      this.performSearch()
      this.$refs.statusFilter.clearFilter()
      this.$refs.startDateFilter.clearFilter()
    },
    checkStartDateInsideRange(intendedStartDate) {
      const start = this.$moment(this.selectedStartDateRange[0])
      const end = this.$moment(this.selectedStartDateRange[1])
      const startDate = this.$moment(intendedStartDate)

      return start <= startDate && startDate <= end
    },
    filterStatus(applicationStatus) {
      // true if matched
      // OR if 'Offered' selected and application status is one of the alternate offered statuses
      return (
        this.selectedStatus.includes(applicationStatus) 
      )
    },
    trackSearch() {
      if (this.searchValue) {
      }
    },
    sendFilterAnalytics(opened) {
      // send analytics data when each filter is closed
      if (!opened) {
        const statusData = this.selectedStatus.length
          ? JSON.stringify(
              this.sortStatuses(this.selectedStatus).map(
                s => EA_STATUS_ABBREV[s] || s
              )
            )
          : null

        // translate "[2020-01-01,2020-12-12]" to "20200101-20201212"
        const dateRangeData = this.selectedStartDateRange.length
          ? JSON.stringify(
              this.selectedStartDateRange
                .map(d => d.replaceAll('-', ''))
                .join('-')
            )
          : null

        const consolidatedData = `${statusData},${dateRangeData}`.replaceAll(
          '"',
          ''
        )

      }
    },
    sortStatuses(statuses) {
      const sortByStatus = [
        EA_STATUS.NEW,
        EA_STATUS.ASSIGNED,
        EA_STATUS.INFO_REQ,
        EA_STATUS.PROCESSING_APP,
        EA_STATUS.APPROVAL_REQ,
        EA_STATUS.REQ_APPROVED,
        EA_STATUS.APPROVAL_REQ_DENIED,
        EA_STATUS.APPROVAL_REQ_APPROVED,
        EA_STATUS.DISMISSED,
        EA_STATUS.DENIED,
        EA_STATUS.UNQUALIFIED
      ]
      return statuses.sort(function(a, b) {
        return sortByStatus.indexOf(a) - sortByStatus.indexOf(b)
      })
    },
  },
  computed: {
    ...mapGetters({
      category: 'selectedCategory',
      eaApplications: 'eaApplications',
      searchApplicationsFilter: 'searchApplicationsFilter',
      statusFilter: 'statusFilter',
      startDateFilter: 'startDateFilter'
    }),
    totalApplicationsDisplayed() {
      return this.applications.length
    },
    isShowLoadMore() {
      return this.totalApplicationsDisplayed > this.pageLength
    },
    isSearchDisplayed() {
      return (
        this.applications.length &&
        (this.searchValue ||
          this.selectedStatus.length ||
          this.selectedStartDateRange.length)
      )
    },
    isFiltered() {
      return (
        this.searchValue ||
        this.selectedStatus.length ||
        this.selectedStartDateRange.length
      )
    },
    isShowSearchFooter() {
      // Note that footer search counters are not displayed if "Load more"
      // is displayed, because "Load more" has its own counter.
      return this.isSearchDisplayed && !this.isShowLoadMore
    },
    visibleApplicationIds() {
      return this.applications
        .slice(0, this.pageLength)
        .map((a) => a.applicationId)
    },
      applicationIds() {
      return this.applications.map((a) => a.applicationID)
    },
    selectedIdsForDownload() {
      let selectedIds = []
      if (this.selectedId != '') {
            selectedIds.push({
              applicationId: this.selectedId,
            })
      }
      return selectedIds
    },
    applications() {
      const applications = this.eaApplications(this.category)
      return (
        applications &&
        applications.filter((a) => {
          let Name =
              (this.searchApplicationsFilter || '') !== ''
                ? searchFilter(
                    [
                      a.application.client.lastName,
                      a.application.client.firstName                     
                    ],
                    this.searchApplicationsFilter
                  )
                : true,
            Status = this.selectedStatus.length
              ? this.filterStatus(a.applicationStatus)
              : true,
            StartDateRange = this.selectedStartDateRange.length
              ? this.checkStartDateInsideRange(a.dateReceived)
              : true   
          return (
            Name &&
            Status && StartDateRange
          )
        })
      )
    },
    statusFilters() {
      const applications = this.eaApplications(this.category)
      const applicationStatuses = [
        ...new Set(
          applications.map(app => {
            // offered statuses all appear as "Offered"
            return  app.applicationStatus
          })
        )
      ]
      return this.sortStatuses(applicationStatuses)
    },
    startDateFilter() {
      const applications = this.eaApplications()
      return { disabled: applications.length ? false : true }
    },
    noResultsFound() {
      if (
        this.searchValue ||
        this.selectedStatus.length ||
        this.selectedStartDateRange.length
      ) {
         return true
      }
      return false
    },
    downloadBtnAriaLabel() {
      return 'Download application'
    }
  }
}
</script>

<style scoped lang="scss">
.ApplicationList {
  overflow: auto;
  min-width: 768px;
  padding-top: 20px;
  ::v-deep .ApplicationStatusChips {
    .v-chip {
      width: 90%;
    }
    .v-chip.text-center {
      justify-content: center;
    }
  }
  .v-card__actions {
    padding: 1rem 3rem;
    background: $grey-6;
  }
}
::v-deep .data-table-footer {
  text-align: right;
  .v-search-results__row {
    background-color: $grey-6;
    height: 68px;
    padding: 1.5rem;
  }
  .value {
    color: $color-primary;
    font-weight: bold;
  }
  .link {
    outline: none;
    text-decoration: underline;
  }
  .mdi-send-outline:before {
    transform: rotate(-45deg);
  }
}
::v-deep .v-input.searchTxtField {
  background-color: transparent;
  input {
    border: 0;
    outline: none;
    background-color: transparent;
  }
  input::placeholder {
    color: $color-placeholder;
  }
  input:-ms-input-placeholder,
  input::-ms-input-placeholder {
    color: $color-placeholder;
  }
  //overridding clear button styles after updating to vuetify 3.2.8
  .v-input__icon--clear button {
    padding: 0;
    border: none;
  }
  .v-input__icon--clear .theme--light.v-icon:before {
    color: white;
    background-color: $color-text-light;
    border-radius: 50%;
    font-size: 16px;
    font-weight: bold;
    padding: 2px;
  }
  .v-input__icon.v-input__icon--append .theme--light.v-icon {
    color: $color-secondary;
  }
}
.alert {
  color: $color-red;
}
// data table
::v-deep .v-data-table {
  padding: 0;
  border: 0;
  table {
    table-layout: fixed;
    .v-data-table__empty-wrapper {
      background-color: white !important; // Prevents hover highlight on "No data available" row
      td {
        padding: 62px 0 !important; // Adds extra tall row height to "No data available" row
        color: $ads-dark-60;
      }
    }
  }
  thead.v-data-table-header {
    th[scope='col'] {
      color: $color-primary;
      font-size: 16px;
      font-weight: normal;
      padding: 20px 8px;
      vertical-align: top;

      &.considerations button {
        word-break: break-all;
        hyphens: auto;
      }
      .v-data-table-header__icon {
        color: $color-primary-lighten-1;
        margin-left: 3px;
      }
      &.active {
        font-weight: bold;
        color: $color-primary;
        .v-data-table-header__icon {
          color: $color-primary;
        }
      }

      //pending applications
      &.date-received {
        width: 110px;
      }

      &.name {
        overflow-wrap: normal;
        word-wrap: normal;
        width: 90px;
      }

      &.dob {
        width: 100px;
      }
      &.scholastic-year {
        width: 80px;
      }

      &.start-date {
        width: 95px;
      }

      &.status {
        width: 150px;
      }
      &.action {
        width: 82px;
        padding-right: 20px;
      }

      //processed applications
      &.last-modified {
        padding-left: 20px;
      }

      //ea applications
      &.select-all {
        width: 60px;
      }
      &.email {
        width: 150px;
      }
      &.phone-no {
        width: 120px;
      }
      &.status-ea {
        width: 150px;
      }
    }
  }

  div.v-data-table__wrapper table tbody {
    tr:not(:last-child) td {
      border-bottom: 1px solid white;
      padding: 25px 8px;
    }
    tr:last-child td {
      border-bottom: 1px solid white;
      padding: 10px;
    }
    td {
      font-size: 14px;
      // overriding vuetify's data table styling
      border-bottom: 0 !important;
    }
  }
}

@include desktop {
  .ApplicationList {
    padding: 1.5rem 3rem;
    min-height: 100%;
  }

  ::v-deep .v-data-table {
    td {
      cursor: default;
    }

    &.pending thead.v-data-table-header th.sortable.date-received {
      padding-left: 20px;
    }

    thead.v-data-table-header th.sortable {
      padding: 20px 10px;

      //pending applications
      &.date-received {
        width: 122px;
      }

      &.name {
        overflow-wrap: normal;
        word-wrap: normal;
        width: auto;
        min-width: 120px;
      }

      &.dob {
        width: 136px;
      }
      &.scholastic-year {
        width: 190px;
      }

      &.start-date {
        width: 150px;
      }

      &.status {
        width: 240px;
      }
      &.action {
        padding-right: 20px;
        width: 102px;
      }
      //processed applications
      &.last-modified {
        padding-left: 20px;
        width: 170px;
      }
    }

    div.v-data-table__wrapper table tbody {
      tr:not(:last-child) td {
        padding: 20px 10px;
      }
      tr:last-child td {
        padding: 20px 10px;
      }
      tr td:first-child {
        padding-left: 20px;
      }
      tr td.status-processed {
        padding-right: 20px;
      }
    }
  }
}
.theme--light.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
  background-color: $grey-darken1 !important;
  color: white !important;
}
#app.v-application
  button.v-btn:not(.v-btn--round):not(.v-btn--outlined):not(.v-btn--text):focus {
  background-color: $color-primary-lighten-1;
}
#app.v-application
  button.v-btn:not(.v-btn--round):not(.v-btn--outlined):not(.v-btn--text):focus:before {
  opacity: 0;
}
#app.v-application
  button.theme--light.v-btn.v-btn:hover:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
  background-color: $color-primary-lighten-1;
}
#app.v-application
  button.v-btn:not(.v-btn--text):not(.v-btn--outlined):hover:before {
  opacity: 0;
}
.theme--light.v-btn.v-btn--disabled .v-icon,
.theme--light.v-btn.v-btn--disabled .v-btn__loading {
  color: white !important;
}
.loadMoreBar {
  text-align: center;
  background-color: $grey-6;
  padding: 1rem;
}
.loadMoreCount {
  position: absolute;
  right: 1rem;
  line-height: 45px;
}
.filterLabel {
  color: $color-placeholder;
}

.search-results-number {
  float: right;
  white-space: nowrap;
}

.data-table-header {
  align: right;
}

    //Checkbox unchecked icon
    .mdi-checkbox-blank-outline {
      color: rgba(0, 0, 0, 0.54) !important;
    }

</style>
